// Canonical mapping between a TipTap document and chat message parts.
//
// User input flows: TipTap doc -> message parts (stored) -> Streamdown.
// This module owns the first hop and its inverse, encoding structure as plain
// text with no renderer-specific syntax (paragraph -> `\n\n`, hardBreak ->
// `\n`, mention -> skill-reference part). How a single `\n` is displayed is a
// rendering concern owned by `MessageResponse`, so the stored text stays
// clean for the LLM, copy-to-clipboard, and future consumers.
//
// Kept free of React/TipTap runtime imports so it is unit-testable as pure
// functions. `serializeEditorToParts` (in `tiptap-input.tsx`) is the thin
// `editor.getJSON()` wrapper that feeds this module.
//
// Single source for the npm package, the shadcn registry, and (via re-export
// shims) the private `@magnus/react` — edit here, never fork.

import type { ArtifactReferencePart, SkillReferencePart } from "./refs";

export type EditorTextPart = { type: "text"; text: string };

/**
 * A staged attachment on send (plan 023): the native AI SDK `file` shape
 * plus the Magnus `data` payload (`uploadId`/`processingStatus`) the
 * server's document-promotion step reads. Never produced by the editor
 * itself — the composer appends these after the editor's parts.
 */
export type EditorFilePart = {
  type: "file";
  url: string;
  mediaType: string;
  filename: string;
  data?: {
    uploadId?: string;
    processingStatus?: string | null;
    /** Carried so a host submit adapter can rebuild the full upload row
     *  (e.g. the flagship's `toSubmissionParts` document-part mapping). */
    size?: number;
    storageKey?: string;
  };
};

export type EditorPart =
  | EditorTextPart
  | SkillReferencePart
  | ArtifactReferencePart
  | EditorFilePart;

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

// Matches either a skill or artifact token in one scan. Group 1 = kind,
// group 2 = id, group 3 = name. Kept in lockstep with the canonical token
// forms produced by `partsToPromptString`.
const COMBINED_REF_RE = /\{\{(skill|artifact):([^|]+)\|([^}]*)\}\}/g;

/**
 * Minimal structural view of a TipTap/ProseMirror document as returned by
 * `editor.getJSON()`. Declared locally (rather than importing TipTap's
 * `JSONContent`) so this module stays free of any editor dependency.
 */
export type EditorNodeJSON = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: EditorNodeJSON[];
};

/**
 * Serialize a TipTap document (its JSON form) into chat message parts.
 * The exact inverse of {@link promptToTiptapDoc}.
 */
export function serializeDocToParts(doc: EditorNodeJSON): EditorPart[] {
  const parts: EditorPart[] = [];

  function pushText(text: string) {
    const last = parts.at(-1);
    if (last && last.type === "text") {
      last.text += text;
    } else {
      parts.push({ type: "text", text });
    }
  }

  const blocks = doc.content ?? [];
  for (let bi = 0; bi < blocks.length; bi++) {
    if (bi > 0) {
      pushText("\n\n");
    }
    const inline = blocks[bi]?.content ?? [];
    for (const node of inline) {
      if (node.type === "text" && node.text) {
        pushText(node.text);
      } else if (node.type === "mention") {
        const attrs = node.attrs ?? {};
        const ref: SkillReferencePart = {
          type: "data-skill-reference",
          data: {
            skillId: asString(attrs.id),
            skillName: asString(attrs.label),
            skillSlug: asString(attrs.slug),
            skillDescription:
              typeof attrs.description === "string"
                ? attrs.description
                : undefined,
          },
        };
        parts.push(ref);
      } else if (node.type === "artifactMention") {
        const attrs = node.attrs ?? {};
        const ref: ArtifactReferencePart = {
          type: "data-artifact-reference",
          data: {
            artifactId: asString(attrs.id),
            artifactName: asString(attrs.label),
            artifactSlug: asString(attrs.slug),
          },
        };
        parts.push(ref);
      } else if (node.type === "hardBreak") {
        pushText("\n");
      }
    }
  }

  return parts;
}

/**
 * Render parts back to a canonical prompt string with `{{skill:id|name}}`
 * tokens for skill references. Non-text, non-reference parts are dropped.
 * Inverse of {@link promptToTiptapDoc} at the string level.
 */
export function partsToPromptString(parts: EditorPart[]): string {
  let out = "";
  for (const p of parts) {
    if (p.type === "text" && "text" in p) {
      out += p.text;
    } else if (p.type === "data-skill-reference") {
      out += `{{skill:${p.data.skillId}|${p.data.skillName}}}`;
    } else if (p.type === "data-artifact-reference") {
      out += `{{artifact:${p.data.artifactId}|${p.data.artifactName}}}`;
    }
  }
  return out;
}

type MentionContent = {
  type: "mention" | "artifactMention";
  attrs: { id: string; label: string };
};
type HardBreakContent = { type: "hardBreak" };
type ParagraphChild =
  | { type: "text"; text: string }
  | MentionContent
  | HardBreakContent;

export type TiptapDoc = {
  type: "doc";
  content: Array<{ type: "paragraph"; content: ParagraphChild[] }>;
};

/**
 * Inverse of {@link serializeDocToParts}: rebuild a TipTap document from a
 * canonical prompt string. The newline encoding mirrors the serializer
 * exactly so a round trip is lossless:
 *
 *   - `\n\n` → paragraph boundary (new paragraph block)
 *   - `\n`   → `hardBreak` node within the current paragraph
 *   - `{{skill:id|name}}` → `mention` node
 */
export function promptToTiptapDoc(prompt: string): TiptapDoc {
  const segments: Array<
    | { type: "text"; text: string }
    | { type: "mention" | "artifactMention"; id: string; label: string }
  > = [];
  const re = new RegExp(COMBINED_REF_RE.source, COMBINED_REF_RE.flags);
  let lastIndex = 0;
  let match = re.exec(prompt);
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        text: prompt.slice(lastIndex, match.index),
      });
    }
    segments.push({
      type: match[1] === "artifact" ? "artifactMention" : "mention",
      id: match[2] ?? "",
      label: match[3] ?? "",
    });
    lastIndex = re.lastIndex;
    match = re.exec(prompt);
  }
  if (lastIndex < prompt.length) {
    segments.push({ type: "text", text: prompt.slice(lastIndex) });
  }

  const paragraphs: TiptapDoc["content"] = [{ type: "paragraph", content: [] }];
  const startParagraph = () => {
    paragraphs.push({ type: "paragraph", content: [] });
  };
  const pushToLast = (node: ParagraphChild) => {
    paragraphs.at(-1)?.content.push(node);
  };
  for (const seg of segments) {
    if (seg.type !== "text") {
      pushToLast({ type: seg.type, attrs: { id: seg.id, label: seg.label } });
      continue;
    }
    // Paragraph breaks first (`\n\n`), then line breaks (`\n`) within each.
    const blocks = seg.text.split("\n\n");
    for (let bi = 0; bi < blocks.length; bi++) {
      if (bi > 0) {
        startParagraph();
      }
      const lines = blocks[bi]?.split("\n") ?? [];
      for (let li = 0; li < lines.length; li++) {
        if (li > 0) {
          pushToLast({ type: "hardBreak" });
        }
        const line = lines[li];
        if (line) {
          pushToLast({ type: "text", text: line });
        }
      }
    }
  }

  return { type: "doc", content: paragraphs };
}
